import { inject, injectable } from 'inversify'
import * as THREE from 'three'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { IPlane } from '@/plugins/cad/module/CADModule/manager/DataManager'
import {
  type CadRenderInvalidator,
  NOOP_CAD_RENDER_INVALIDATOR,
} from '@/plugins/cad/module/CADModule/renderInvalidation'
import { TYPES } from '@/plugins/cad/module/container/types'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { EngineContext } from '@/runtime/EngineContext'
import { getLoggerManager, LogLevel } from '@/shared/logging'

const GRID_DEPTH_OFFSET = -0.1
const MIN_VISIBLE_SIZE = 1
const MAX_GRID_LINES_PER_AXIS = 2_000
const INVALID_RANGE_LOG_INTERVAL_MS = 5_000
const loggerManager = getLoggerManager()
const log = loggerManager.getLogger('cad:grid')
const VIEWPORT_CORNERS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
] as const

interface ILocalBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

interface IPlaneBasis {
  origin: THREE.Vector3
  xAxis: THREE.Vector3
  yAxis: THREE.Vector3
  zAxis: THREE.Vector3
}

@injectable()
export class GridHelper {
  @inject(TYPES.EngineContext)
  private readonly ctx!: EngineContext

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.RenderInvalidator)
  private readonly renderInvalidator: CadRenderInvalidator = NOOP_CAD_RENDER_INVALIDATOR

  private enabled: boolean = false
  private gridObject: THREE.LineSegments | null = null
  private isFrameListenerBound: boolean = false
  private lastInvalidRangeLogAt = Number.NEGATIVE_INFINITY

  public isEnabled(): boolean {
    return this.enabled
  }

  public setEnabled(enabled: boolean) {
    if (this.enabled === enabled) {
      return
    }

    this.enabled = enabled
    if (this.enabled) {
      this.bindFrameListener()
      this.updateGridAndScale()
      this.renderInvalidator.invalidate('cad.grid.enabled')
    } else {
      this.unbindFrameListener()
      this.lastInvalidRangeLogAt = Number.NEGATIVE_INFINITY
      if (this.gridObject) {
        DrawUtils.disposeObject(this.gridObject)
        this.gridObject = null
      }
      this.renderInvalidator.invalidate('cad.grid.disabled')
    }
  }

  private updateGridAndScale() {
    if (!this.enabled) {
      return
    }

    const rawCam = this.ctx.getService('ViewportCamera').getActiveCamera()
    if (!(rawCam as THREE.OrthographicCamera).isOrthographicCamera) {
      return
    }

    const cam = rawCam as THREE.OrthographicCamera
    const plane = this.viewManager.workPlane
    const basis = getPlaneBasis(plane)
    const bounds = this.getVisibleLocalBounds(cam, basis)
    const visibleLocalWidth = Math.max(bounds.maxX - bounds.minX, MIN_VISIBLE_SIZE)
    const visibleLocalHeight = Math.max(bounds.maxY - bounds.minY, MIN_VISIBLE_SIZE)
    const majorStep = this.getNiceStep(Math.max(visibleLocalWidth, visibleLocalHeight) / 10)
    const minorStep = majorStep / 5

    const startXIdx = Math.floor(bounds.minX / minorStep) - 1
    const endXIdx = Math.ceil(bounds.maxX / minorStep) + 1
    const startYIdx = Math.floor(bounds.minY / minorStep) - 1
    const endYIdx = Math.ceil(bounds.maxY / minorStep) + 1
    const xLineCount = endXIdx - startXIdx + 1
    const yLineCount = endYIdx - startYIdx + 1

    const hasFiniteRange =
      Number.isFinite(bounds.minX) &&
      Number.isFinite(bounds.maxX) &&
      Number.isFinite(bounds.minY) &&
      Number.isFinite(bounds.maxY) &&
      Number.isFinite(visibleLocalWidth) &&
      Number.isFinite(visibleLocalHeight) &&
      Number.isFinite(majorStep) &&
      majorStep > 0 &&
      Number.isFinite(minorStep) &&
      minorStep > 0
    const hasSafeIndices =
      Number.isSafeInteger(startXIdx) &&
      Number.isSafeInteger(endXIdx) &&
      Number.isSafeInteger(startYIdx) &&
      Number.isSafeInteger(endYIdx) &&
      Number.isSafeInteger(xLineCount) &&
      Number.isSafeInteger(yLineCount)
    const hasSafeLineCounts =
      xLineCount > 0 &&
      xLineCount <= MAX_GRID_LINES_PER_AXIS &&
      yLineCount > 0 &&
      yLineCount <= MAX_GRID_LINES_PER_AXIS

    if (!hasFiniteRange || !hasSafeIndices || !hasSafeLineCounts) {
      if (log.isEnabled(LogLevel.ERROR)) {
        const now = loggerManager.now()
        if (now - this.lastInvalidRangeLogAt >= INVALID_RANGE_LOG_INTERVAL_MS) {
          let reason = 'excessiveLineCount'
          if (!hasFiniteRange) {
            reason = 'nonFiniteRange'
          } else if (!hasSafeIndices) {
            reason = 'unsafeIndices'
          }
          log.error(
            `CAD grid skipped reason=${reason}` +
              ` bounds=${bounds.minX},${bounds.maxX},${bounds.minY},${bounds.maxY}` +
              ` lineCounts=${xLineCount},${yLineCount}` +
              ` maxLinesPerAxis=${MAX_GRID_LINES_PER_AXIS}`
          )
          this.lastInvalidRangeLogAt = now
        }
      }
      return
    }

    const minX = startXIdx * minorStep
    const maxX = endXIdx * minorStep
    const minY = startYIdx * minorStep
    const maxY = endYIdx * minorStep

    const vertices: number[] = []
    const colors: number[] = []
    const colorMajor = new THREE.Color(0x444444)
    const colorMinor = new THREE.Color(0x222222)

    for (let i = startXIdx; i <= endXIdx; i++) {
      const x = i * minorStep
      const c = i % 5 === 0 ? colorMajor : colorMinor
      vertices.push(x, minY, 0, x, maxY, 0)
      colors.push(c.r, c.g, c.b, c.r, c.g, c.b)
    }

    for (let j = startYIdx; j <= endYIdx; j++) {
      const y = j * minorStep
      const c = j % 5 === 0 ? colorMajor : colorMinor
      vertices.push(minX, y, 0, maxX, y, 0)
      colors.push(c.r, c.g, c.b, c.r, c.g, c.b)
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

    if (!this.gridObject) {
      this.gridObject = new THREE.LineSegments(
        geometry,
        new THREE.LineBasicMaterial({
          vertexColors: true,
          depthWrite: false,
          depthTest: false,
          transparent: true,
          opacity: 0.8,
        })
      )
      this.gridObject.frustumCulled = false
      this.viewManager.attachTempObject(this.gridObject)
    } else {
      this.gridObject.geometry.dispose()
      this.gridObject.geometry = geometry
    }

    this.updateGridTransform()
  }

  private getVisibleLocalBounds(cam: THREE.OrthographicCamera, basis: IPlaneBasis): ILocalBounds {
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(basis.zAxis, basis.origin)
    const localPoints: THREE.Vector2[] = []

    for (const [ndcX, ndcY] of VIEWPORT_CORNERS) {
      const hit = this.intersectCameraRayWithPlane(cam, plane, ndcX, ndcY)
      if (hit) {
        localPoints.push(this.worldToPlaneLocal(hit, basis))
      }
    }

    if (localPoints.length >= 2) {
      return this.getLocalBounds(localPoints)
    }

    return this.getFallbackLocalBounds(cam, plane, basis)
  }

  private intersectCameraRayWithPlane(
    cam: THREE.OrthographicCamera,
    plane: THREE.Plane,
    ndcX: number,
    ndcY: number
  ): THREE.Vector3 | null {
    const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(cam)
    const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(cam)
    const direction = farPoint.sub(nearPoint)
    if (direction.lengthSq() === 0) {
      return null
    }

    const hit = new THREE.Vector3()
    return new THREE.Ray(nearPoint, direction.normalize()).intersectPlane(plane, hit)
  }

  private getFallbackLocalBounds(
    cam: THREE.OrthographicCamera,
    plane: THREE.Plane,
    basis: IPlaneBasis
  ): ILocalBounds {
    const centerHit = this.intersectCameraRayWithPlane(cam, plane, 0, 0)
    const centerWorld = centerHit ?? plane.projectPoint(cam.position, new THREE.Vector3())
    const center = this.worldToPlaneLocal(centerWorld, basis)
    const visibleWorldWidth = Math.max(
      Math.abs((cam.right - cam.left) / cam.zoom),
      MIN_VISIBLE_SIZE
    )
    const visibleWorldHeight = Math.max(
      Math.abs((cam.top - cam.bottom) / cam.zoom),
      MIN_VISIBLE_SIZE
    )

    return {
      minX: center.x - visibleWorldWidth / 2,
      maxX: center.x + visibleWorldWidth / 2,
      minY: center.y - visibleWorldHeight / 2,
      maxY: center.y + visibleWorldHeight / 2,
    }
  }

  private getLocalBounds(points: THREE.Vector2[]): ILocalBounds {
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    for (const point of points) {
      minX = Math.min(minX, point.x)
      maxX = Math.max(maxX, point.x)
      minY = Math.min(minY, point.y)
      maxY = Math.max(maxY, point.y)
    }

    return { minX, maxX, minY, maxY }
  }

  private worldToPlaneLocal(point: THREE.Vector3, basis: IPlaneBasis): THREE.Vector2 {
    const offset = point.clone().sub(basis.origin)
    return new THREE.Vector2(offset.dot(basis.xAxis), offset.dot(basis.yAxis))
  }

  private getNiceStep(rawStep: number): number {
    if (!Number.isFinite(rawStep) || rawStep <= 0) {
      return 1
    }

    const exponent = Math.floor(Math.log10(rawStep))
    const fraction = rawStep / 10 ** exponent
    const niceFraction = fraction >= 5 ? 5 : fraction >= 2 ? 2 : 1
    return niceFraction * 10 ** exponent
  }

  private updateGridTransform() {
    if (!this.gridObject) {
      return
    }

    this.gridObject.quaternion.identity()
    this.gridObject.position.set(0, 0, GRID_DEPTH_OFFSET)
    this.gridObject.updateMatrixWorld(true)
  }

  private bindFrameListener() {
    if (this.isFrameListenerBound) {
      return
    }
    this.ctx.events.on('frame.begin', this.handleFrameBegin)
    this.isFrameListenerBound = true
  }

  private unbindFrameListener() {
    if (!this.isFrameListenerBound) {
      return
    }
    this.ctx.events.off('frame.begin', this.handleFrameBegin)
    this.isFrameListenerBound = false
  }

  private readonly handleFrameBegin = () => {
    this.updateGridAndScale()
  }
}

function getPlaneBasis(plane: IPlane): IPlaneBasis {
  const { origin, normal, up } = plane
  const zAxis = normal.clone().normalize()
  const xAxis = new THREE.Vector3().crossVectors(up, zAxis)
  if (xAxis.lengthSq() === 0) {
    const fallbackUp =
      Math.abs(zAxis.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0)
    xAxis.crossVectors(fallbackUp, zAxis)
  }
  xAxis.normalize()

  const yAxis = new THREE.Vector3().crossVectors(zAxis, xAxis).normalize()
  return {
    origin,
    xAxis,
    yAxis,
    zAxis,
  }
}
